/**
 * @description: TravellerDetails Common Component
 * @author: Common Components Library
 * @date: 2024
 * A reusable component for managing traveller/passenger details with CRUD operations
 */

import React, { useEffect, useState } from 'react';
import { FaPencilAlt, FaRegUserCircle, FaTrashAlt } from 'react-icons/fa';
import {
  Button,
  Card,
  Col,
  Container,
  Row,
  Modal,
  Alert,
} from 'react-bootstrap';
import MetaTags from 'react-meta-tags';
import { isNotNull } from '../validators/validators';
import DeletePopup from '../popups/DeletePopup';
import DataLoader from '../loaders/DataLoader';
import styles from './TravellerDetails.module.scss';

function TravellerDetails({
  // Required props
  companyName,
  pageTitle,
  pageDescription,
  // Data and operations
  passengersData,
  onFetchPassengers,
  onDeletePassenger,
  // UI Components
  AddPassengerComponent,
  EditPassengerComponent,
  SideNavComponent,
  // User data
  userData = null,
  userInfo = null,
  dataAvailability,
  noData,
  // Customization
  showSideNav = true,
  // Alert customization
  alertDuration = 5000,
  // Modal customization
  modalSize = 'xl',
}) {
  // State management
  const [addPassengerShow, setAddPassengerShow] = useState(false);
  const [editPassengerShow, setEditPassengerShow] = useState(false);
  const [alertShow, setAlertShow] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertVariant, setAlertVariant] = useState('');
  const [showPopup, setShowPopup] = useState(false);
  const [rowData, setRowData] = useState('');
  const [editPassengerData, setEditPassengerData] = useState([]);

  // Initialize data on component mount
  useEffect(() => {
    if (onFetchPassengers) {
      onFetchPassengers();
    }
  }, []);

  // Update data availability when passengers data changes
  // useEffect(() => {
  //   console.log('passengerDaata-->', passengersData);

  //   if (passengersData && passengersData?.length > 0) {
  //     setDataAvailability(true);
  //     setNoData(false);
  //   } else {
  //     setDataAvailability(false);
  //     setNoData(true);
  //   }
  // }, [passengersData]);

  // if (response && response.length > 0) {
  //       setPassengersData(response);
  //       setDataAvailability(true);
  //     } else {
  //       setDataAvailability(false);
  //       setNoData(true);
  //     }

  // Handle close passenger modal
  const handleClosePassenger = (msg, res) => {
    if (res === 'success') {
      setAddPassengerShow(false);
      setAlertMessage('Added Passenger Successfully');
      setAlertShow(true);
      setAlertVariant('success');
      hideMessage();
      // if (onPassengerAdded) {
      //   onPassengerAdded();
      // }
    } else {
      setAddPassengerShow(false);
      setAlertMessage(msg || 'Error adding passenger');
      setAlertShow(true);
      setAlertVariant('danger');
      hideMessage();
      // if (onError) {
      //   onError(msg || 'Error adding passenger');
      // }
    }
  };

  // Handle show passenger modal
  const handleShowPassenger = () => setAddPassengerShow(true);

  // Handle close edit passenger modal
  const handleCloseEditPassenger = () => {
    setEditPassengerShow(false);
    setAlertMessage('Updated Successfully');
    setAlertShow(true);
    setAlertVariant('success');
    hideMessage();
    // if (onPassengerEdited) {
    //   onPassengerEdited();
    // }
  };

  // Handle show edit passenger modal
  const handleShowEditPassenger = () => setEditPassengerShow(true);

  // Handle delete data
  const handleDeleteData = (pass) => {
    setShowPopup(true);
    setRowData(pass);
  };

  // Handle delete confirmation
  const handleDelete = async () => {
    try {
      // Call delete function
      const res = await onDeletePassenger(rowData);
      setShowPopup(false);

      if (res && res.status === 'success') {
        if (onFetchPassengers) {
          await onFetchPassengers();
        }

        // Show success message
        setAlertShow(true);
        setAlertMessage('Deleted Passenger Successfully');
        setAlertVariant('success');
        hideMessage();
      } else {
        if (onFetchPassengers) {
          await onFetchPassengers();
        }
        setAlertShow(true);
        setAlertMessage('Deleted Passenger Successfully');
        setAlertVariant('success');
        hideMessage();
      }
    } catch (error) {
      console.error('Delete passenger error:', error);
      setShowPopup(false);
      setAlertShow(true);
      setAlertMessage('Error deleting passenger');
      setAlertVariant('danger');
      hideMessage();
    }
  };

  // Hide notification
  const hideMessage = () => {
    setTimeout(() => {
      setAlertShow(false);
      setAlertMessage('');
      setAlertVariant('');
    }, alertDuration);
  };

  // Handle edit
  const handleEdit = (pass) => {
    setEditPassengerData(pass);
    handleShowEditPassenger();
  };

  // Render passenger name with fallback
  const renderPassengerName = (pass, userEmail) => {
    const name =
      isNotNull(pass.fsnm) && isNotNull(pass.lsnm)
        ? `${pass.fsnm} ${pass.lsnm}`
        : userEmail || 'Unknown';

    const title =
      pass.tle || pass.typ ? (
        <small className="ms-2">
          {'(' +
            (pass.typ && pass.typ !== null ? pass.typ + ' - ' : '') +
            (pass.tle &&
              (pass.tle === 'M' ? 'Male' : pass.tle === 'F' ? 'Female' : '')) +
            ')'}
        </small>
      ) : null;

    return (
      <strong>
        {name}
        {title}
      </strong>
    );
  };

  return (
    <>
      <MetaTags>
        <title>
          {pageTitle} | {companyName}
        </title>
        <meta name="description" content={pageDescription} />
      </MetaTags>

      <Alert
        className="notification"
        variant={alertVariant}
        show={alertShow}
        onClose={() => setAlertShow(false)}
        dismissible
      >
        {alertMessage}
      </Alert>


      {/* <div className="myACcountBanner"></div> */}

      <Container className={`pageContainer cont-top maContainer `}>
        <Row>
          {showSideNav && SideNavComponent && (
            <Col md="3">
              <SideNavComponent
                pageName="P_D"
                userData={userData}
                // {...sideNavProps}
              />
            </Col>
          )}
          <Col md={showSideNav ? '9' : '12'}>
            <Card>
              <Card.Header>
                <div className="myAccHeader d-flex flex-column">
                  <h2>{pageTitle}</h2>
                  <span className="mt-1">{pageDescription}</span>
                </div>
                <Button
                  onClick={handleShowPassenger}
                  size="sm"
                  variant="btn btn-primary"
                >
                  Add Traveller
                </Button>
              </Card.Header>
              <Card.Body>
                <div className="passengers-info">
                  {dataAvailability ? (
                    passengersData &&
                    passengersData.map((pass, i) => (
                      <Row
                        key={i}
                        className="passenger-data-list d-flex align-items-flex-start justify-content-between"
                      >
                        <Col
                          md="4"
                          className="pass-details d-flex align-items-center"
                        >
                          <div className="user-img">
                            <FaRegUserCircle />
                          </div>
                          <div className="pass-data d-flex flex-column">
                            {renderPassengerName(pass, userData?.em)}
                            <span>
                              DOB: {isNotNull(pass.dob) ? pass.dob : '-'}
                            </span>
                          </div>
                        </Col>
                        <Col md="2" className="pass-details d-flex flex-column">
                          <strong>Passport No</strong>
                          <span>
                            {isNotNull(pass.pspno) ? pass.pspno : '-'}
                          </span>
                        </Col>
                        <Col md="2" className="pass-details d-flex flex-column">
                          <strong>Nationality</strong>
                          <span>{isNotNull(pass.ntly) ? pass.ntly : '-'}</span>
                        </Col>
                        <Col
                          md="3"
                          className="d-flex align-items-center justify-content-end"
                        >
                          <Button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => handleEdit(pass)}
                          >
                            <FaPencilAlt />
                          </Button>
                          {pass.lpx !== 1 && (
                            <Button
                              className="btn btn-outline-danger btn-sm ms-2"
                              onClick={() => handleDeleteData(pass)}
                            >
                              <FaTrashAlt />
                            </Button>
                          )}
                        </Col>
                      </Row>
                    ))
                  ) : (
                    <DataLoader noData={noData} />
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Add Passenger Modal */}
      <Modal
        show={addPassengerShow}
        onHide={() => setAddPassengerShow(false)}
        size={modalSize}
        className="travellerPopup"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <strong>Add Traveller</strong>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {AddPassengerComponent ? (
            <AddPassengerComponent
              close={handleClosePassenger}
              onFetchPassengers={onFetchPassengers}
            />
          ) : (
            <div>Add Passenger Component not provided</div>
          )}
        </Modal.Body>
      </Modal>

      {/* Edit Passenger Modal */}
      <Modal
        show={editPassengerShow}
        onHide={() => setEditPassengerShow(false)}
        size={modalSize}
        className="travellerPopup"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <strong>Edit Traveller</strong>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {EditPassengerComponent ? (
            <EditPassengerComponent
              rowData={editPassengerData}
              close={handleCloseEditPassenger}
              userData={userInfo}
              onFetchPassengers={onFetchPassengers}
            />
          ) : (
            <div>Edit Passenger Component not provided</div>
          )}
        </Modal.Body>
      </Modal>

      {/* Delete Confirmation Popup */}
      <DeletePopup
        show={showPopup}
        yes={handleDelete}
        pass={rowData}
        close={() => setShowPopup(false)}
      />
    </>
  );
}

export default TravellerDetails;
